import { themeEvents } from "../core/events";

const RUNTIME_KEY = "__rebalanceContentSquareRuntime";
const CONTENTSQUARE_SCRIPT_SELECTOR = 'script[src*="t.contentsquare.net/uxa/"]';

interface ContentsquareRuntime {
  cleanup: () => void;
}

interface ContentsquareRuntimeWindow extends Window {
  [RUNTIME_KEY]?: ContentsquareRuntime;
}

function dedupeContentsquareScripts(): number {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>(CONTENTSQUARE_SCRIPT_SELECTOR)
  );

  if (scripts.length <= 1) {
    return scripts.length;
  }

  scripts.slice(1).forEach((duplicateScript) => {
    duplicateScript.parentNode?.removeChild(duplicateScript);
  });
  return 1;
}

function hasContentsquareQueue(): boolean {
  const queue = window._uxa;
  if (!queue || typeof queue.push !== "function") {
    return false;
  }

  return true;
}

function syncContentsquare(reason: string): void {
  const scriptCount = dedupeContentsquareScripts();
  const queueReady = hasContentsquareQueue();

  document.documentElement.setAttribute("data-build-contentsquare-adapter", "ready");
  document.documentElement.setAttribute("data-build-contentsquare-script-count", String(scriptCount));
  document.documentElement.setAttribute(
    "data-build-contentsquare-last-sync",
    `${reason}:${queueReady ? "queue-ready" : "scripts-only"}`
  );
  window.dispatchEvent(
    new CustomEvent("rebalance:contentsquare:sync", {
      detail: { reason, queueReady, scriptCount }
    })
  );
}

export function initContentsquare(): void {
  const runtimeWindow = window as ContentsquareRuntimeWindow;
  if (runtimeWindow[RUNTIME_KEY]) {
    return;
  }

  let syncTimer = 0;

  const scheduleSync = (reason: string, delayMs = 160): void => {
    if (syncTimer) {
      window.clearTimeout(syncTimer);
    }

    syncTimer = window.setTimeout(() => {
      syncTimer = 0;
      syncContentsquare(reason);
    }, delayMs);
  };

  const handleGridUpdated = (): void => scheduleSync("collection-grid-updated");
  const handleWindowLoad = (): void => scheduleSync("window-load");
  const unlistenRouteInitialized = themeEvents.on("route:initialized", ({ template }) => {
    scheduleSync(`route-initialized:${template}`);
  });

  document.addEventListener("baseline:collection:grid-updated", handleGridUpdated);
  window.addEventListener("load", handleWindowLoad);

  runtimeWindow[RUNTIME_KEY] = {
    cleanup: () => {
      document.removeEventListener("baseline:collection:grid-updated", handleGridUpdated);
      window.removeEventListener("load", handleWindowLoad);
      unlistenRouteInitialized();
      if (syncTimer) {
        window.clearTimeout(syncTimer);
        syncTimer = 0;
      }
    }
  };

  scheduleSync("adapter-init", 0);
}
